import { ref } from 'vue';
import axios, { AxiosRequestConfig } from 'axios';
import { useRouter } from 'vue-router';
import { useAuth } from '@/hooks/useAuth';
import { toast } from '@/utils';
interface RequestConfig extends AxiosRequestConfig {
  showError?: boolean;
}

interface CustomRequestConfig extends AxiosRequestConfig {
  needLogin?: boolean;
  showLoading?: boolean;
  showError?: boolean;
  errorMessage?: string;
}

const defaultCustomConfig: CustomRequestConfig = {
  needLogin: true,
  showLoading: true,
  showError: false,
  errorMessage: '網絡錯誤'
};

const axiosInstance = axios.create();

let isInterceptorSet = false;

const setupInterceptors = () => {
  if (isInterceptorSet) {
    return;
  }
  axiosInstance.interceptors.request.use(
    config => {
      const token = useAuth().getToken();
      if (token) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${token}`;
      }

      return config;
    },
    error => {
      return Promise.reject(error);
    }
  );
  isInterceptorSet = true;
};

export function useRequest() {
  const loading = ref(false);
  const error = ref<Error | null>(null);
  const router = useRouter();

  const baseUrl = import.meta.env.VITE_API_BASEURL;
  const userPreferredLanguage = 'zh-CN';

  setupInterceptors();

  const request = async <T>(
    config: RequestConfig,
    customConfig: CustomRequestConfig = {}
  ): Promise<T> => {
    const finalCustomConfig = { ...defaultCustomConfig, ...customConfig };

    if (finalCustomConfig.showLoading) {
      loading.value = true;
    }

    if (finalCustomConfig.needLogin) {
      if (!useAuth().isLogin()) {
        router?.push('/select');
        return Promise.reject(new Error('need login'));
      }
    }

    error.value = null;

    try {
      const axiosConfig: AxiosRequestConfig = {
        ...config,
        ...finalCustomConfig,
        baseURL: baseUrl,
        headers: {
          'Content-Type': 'application/json',
          'Accept-Language': userPreferredLanguage,
          ...config.headers
        }
      };

      const response = await axiosInstance(axiosConfig);
      return response.data;
    } catch (err: unknown) {
      error.value = err as Error;
      if (finalCustomConfig.showError) {
        toast.error(finalCustomConfig.errorMessage!);
      }
      throw err;
    } finally {
      loading.value = false;
    }
  };

  return {
    request,
    loading,
    error
  };
}
